import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MapPin } from '@ooh/ui/components/map-view';
import type { MapPinsQuery } from '@ooh/contracts';
import { filterOptionsQuery, mapPinsQuery } from '../../features/media-units/api/queries';
import type { WithCallWithAuth } from '../../features/media-units/api/queries';
import {
  buildFilterFields,
  buildFilterDimensionsFromPanelValue,
} from '../../features/media-units/lib/filter-fields';
import { useLocationFilterPanel } from '../../features/media-units/hooks/use-location-filter-panel';
import { buildRangePillLabels } from '../../lib/build-range-pill-labels';
import { useMapFilterLabels } from './use-map-filter-labels';
import { useHasPermission } from '../../hooks/use-has-permission';
import { useHasWildcardPermission } from '../../hooks/use-has-wildcard-permission';

interface PoiFilterParams {
  nearPoiId?: string;
  nearRadius?: number;
  nearKey?: string;
  nearValue?: string;
}

type FilterValue = Record<string, string[]>;

// eslint-disable-next-line max-lines-per-function -- aggregates pins query, filter fields, and labels — natural cohesion
export function useMapPinsData(
  auth: WithCallWithAuth,
  filterValue: FilterValue,
  poiFilterParams: PoiFilterParams,
  searchTerm: string | undefined,
  setFilterValue: (v: FilterValue) => void,
  noPhoto: boolean,
  availabilityRange: string[],
) {
  const { t } = useTranslation('map');

  const nearPoiParams = React.useMemo(() => {
    if (poiFilterParams.nearPoiId && poiFilterParams.nearRadius) {
      return {
        near_poi_id: poiFilterParams.nearPoiId,
        near_poi_radius_m: poiFilterParams.nearRadius,
      };
    }
    if (!poiFilterParams.nearKey || !poiFilterParams.nearValue || !poiFilterParams.nearRadius) {
      return {};
    }
    return {
      near_poi_key: poiFilterParams.nearKey,
      near_poi_value: poiFilterParams.nearValue,
      near_poi_radius_m: poiFilterParams.nearRadius,
    };
  }, [poiFilterParams]);

  // Decomposes filterValue's array/range/scalar-text FilterPanel value through
  // buildFilterDimensionsFromPanelValue (shared with the catalog's
  // panelValueToFilters) instead of raw-spreading it — a raw spread only
  // happened to work while every /map field was array-valued; it silently
  // broke for the new range fields ([min,max] pairs needing `_min`/`_max`
  // scalar keys) and scalar-text fields ([value] needing an unwrapped string)
  // — see map-catalog-filter-parity.
  const combinedFilters = React.useMemo(() => {
    const dimensions = buildFilterDimensionsFromPanelValue(filterValue);
    return {
      ...dimensions,
      ...nearPoiParams,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(noPhoto ? { noPhoto: true } : {}),
      ...(availabilityRange[0] && availabilityRange[1]
        ? { availabilityFrom: availabilityRange[0], availabilityTo: availabilityRange[1] }
        : {}),
    } as unknown as MapPinsQuery;
  }, [filterValue, nearPoiParams, searchTerm, noPhoto, availabilityRange]);

  const { data, isLoading, isError, refetch } = useQuery({
    ...mapPinsQuery(auth, combinedFilters),
    placeholderData: (prev) => prev,
  });

  const mapPins: MapPin[] = React.useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((pin) => ({
      id: pin.id,
      latitude: pin.latitude,
      longitude: pin.longitude,
      category: pin.mediaType ?? 'inne',
    }));
  }, [data]);

  const { data: filterOptionsData } = useQuery(filterOptionsQuery(auth));
  const filterFieldLabels = useMapFilterLabels(t);
  // Supplier details are gated by their own supplier.view capability — mirrors
  // the catalog route (routes/media-units/index.tsx), which resolves the same
  // check and passes it into buildFilterFields. Without this, Mapa showed the
  // Dostawca filter to every caller regardless of grant (CU-86cbenzvw).
  const canViewSupplierDetails = useHasPermission('supplier.view');
  // Mirrors the catalog route (routes/media-units/index.tsx) resolving the
  // same two gates and opting into the catalog-only dimensions (material,
  // notes, the numeric ranges, etc.) — these are now fully wired end-to-end
  // into MapPinsQuerySchema too (map-catalog-filter-parity).
  const canViewCosts = useHasPermission('cost.view');
  const canViewRent = useHasWildcardPermission('cost.view_rent');
  const filterFields = React.useMemo(
    () =>
      buildFilterFields(filterOptionsData, filterFieldLabels, {
        canViewSupplierDetails,
        canViewCosts,
        canViewRent,
        includeCatalogOnlyFields: true,
      }),
    [filterOptionsData, filterFieldLabels, canViewSupplierDetails, canViewCosts, canViewRent],
  );

  // Combined "Lokalizacja" pill: merges region/county/agglomeration/city into
  // one FilterPanel-facing value and redistributes updates back on change —
  // mirrors the catalog's useLocationFilterPanel wiring (use-media-units-page.ts).
  const { panelFilterValue, handlePanelFiltersChange } = useLocationFilterPanel(
    filterOptionsData,
    filterFieldLabels,
    filterValue,
    setFilterValue,
  );

  const filterPanelLabels = React.useMemo(
    () => ({
      allLabel: t('filters.allLabel'),
      searchPlaceholder: t('filters.searchPlaceholder'),
      getSelectedCountLabel: (c: number) => t('filters.selectedCountLabel', { n: c }),
      getClearAriaLabel: (fieldId: string) => t('filters.getClearAriaLabel', { fieldId }),
      clearAllLabel: t('filters.clearAllLabel'),
      addFilterLabel: t('filters.addFilterLabel'),
    }),
    [t],
  );

  // RangePill's own labels (Min/Max/Zastosuj/Wyczyść) — REQUIRED once
  // includeCatalogOnlyFields renders any `type: 'range'` field (all 18 numeric
  // dimensions, above). Missing this crashes RangePill at render time reading
  // `rangeLabels.minLabel` off `undefined` — FilterPanel's `rangeLabels` prop
  // is optional so TypeScript does not catch an omitted value
  // (map-catalog-filter-parity regression). Shared with the catalog via
  // buildRangePillLabels so the two can never drift out of sync again.
  const rangeLabels = React.useMemo(() => buildRangePillLabels(t), [t]);

  return {
    mapPins,
    filterFields,
    filterPanelLabels,
    rangeLabels,
    panelFilterValue,
    handlePanelFiltersChange,
    isLoading,
    isError,
    refetch,
    data,
  };
}
